import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { useCampaign } from "../contexts/CampaignContext";

function useCountdown(endsAt) {
    const [remaining, setRemaining] = useState(() => diff(endsAt));
    useEffect(() => {
        if (!endsAt) return;
        setRemaining(diff(endsAt));
        const t = setInterval(() => setRemaining(diff(endsAt)), 1000);
        return () => clearInterval(t);
    }, [endsAt]);
    return remaining;
}

function diff(endsAt) {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return null;
    const total = Math.floor(ms / 1000);
    return {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
    };
}

function Unit({ value, label }) {
    return (
        <div className="flex flex-col items-center leading-none">
            <span className="font-heading font-black text-base sm:text-lg tabular-nums text-white drop-shadow">
                {String(value).padStart(2, "0")}
            </span>
            <span className="text-[8px] font-mono uppercase tracking-[0.2em] text-white/70 mt-0.5">{label}</span>
        </div>
    );
}

export default function CyberDayBanner() {
    const { campaign } = useCampaign();
    const time = useCountdown(campaign?.ends_at);

    if (!campaign || !campaign.active) return null;

    return (
        <Link
            to={campaign.cta_link || "/plugins"}
            data-testid="cyberday-banner"
            className="group relative block overflow-hidden"
            style={{
                background: "linear-gradient(90deg, #FF2E9A 0%, #7C3AED 48%, #22D3EE 100%)",
            }}
        >
            <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[radial-gradient(circle_at_20%_50%,white,transparent_40%)]" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-center">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-white fill-white animate-pulse" strokeWidth={2} />
                    <span className="font-heading font-black text-sm sm:text-base uppercase tracking-tight text-white">
                        {campaign.title}
                    </span>
                </div>
                {campaign.subtitle && (
                    <span className="hidden md:inline text-xs text-white/85 font-mono">{campaign.subtitle}</span>
                )}
                {time && (
                    <div className="flex items-center gap-2 sm:gap-3" data-testid="cyberday-countdown">
                        {time.days > 0 && <Unit value={time.days} label="Days" />}
                        {time.days > 0 && <span className="text-white/50 font-black">:</span>}
                        <Unit value={time.hours} label="Hrs" />
                        <span className="text-white/50 font-black">:</span>
                        <Unit value={time.minutes} label="Min" />
                        <span className="text-white/50 font-black">:</span>
                        <Unit value={time.seconds} label="Sec" />
                    </div>
                )}
                <span className="hidden sm:inline-flex items-center gap-1 bg-white text-[#7C3AED] font-mono font-bold text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full group-hover:scale-105 transition-transform">
                    {campaign.cta_text || "View offers"} →
                </span>
            </div>
        </Link>
    );
}
